/**
 * Los filtros marcables del calendario (spec 015 · FR-021).
 *
 * Sustituyen a los dos desplegables de «todos los profesionales» / «todos los
 * servicios», que solo dejaban elegir UNO o TODOS: con cuatro profesionales, ver
 * a dos concretos no se podía.
 *
 * Viven aquí y no dentro de la página porque `calendario.tsx` ya pasa del límite
 * de líneas del linter, y porque esto es presentación pura: no sabe qué es un
 * profesional ni qué es un servicio.
 */
import type React from 'react'

/**
 * Una fila de filtros marcables (FR-021).
 *
 * Lleva su propio «ver todos» y solo aparece cuando hay algo oculto: un botón de
 * limpiar siempre visible ocupa sitio y no dice nada, y cuando SÍ hay filtro es
 * justo lo que se busca —de otro modo hay que acordarse de cuáles se apagaron—.
 */
export function FilaFiltro({
    rotulo,
    hayOcultos,
    alVerTodos,
    children,
}: {
    rotulo: string
    hayOcultos: boolean
    alVerTodos: () => void
    children: React.ReactNode
}) {
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 w-24 shrink-0 text-[11px] uppercase tracking-wide text-neutral-500">
                {rotulo}
            </span>
            {children}
            {hayOcultos && (
                <button
                    onClick={alVerTodos}
                    className="ml-1 text-[11px] text-sky-400 underline decoration-dotted hover:text-sky-300"
                >
                    ver todos
                </button>
            )}
        </div>
    )
}

/**
 * Un filtro que se marca y se desmarca.
 *
 * Apagado NO se esconde: se queda tachado y en gris. Que siga ahí es lo que
 * permite volver a encenderlo, y lo que recuerda que ese profesional existe
 * aunque ahora no se vea — desaparecerlo dejaría un calendario incompleto sin
 * ninguna pista de por qué.
 *
 * El color va en `style` porque sale de la base: Tailwind compila las clases que
 * ve escritas, así que una clase construida con una variable no existiría.
 */
export function ChipFiltro({
    activo,
    color,
    onClick,
    children,
}: {
    activo: boolean
    color?: string | undefined
    onClick: () => void
    children: React.ReactNode
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={activo}
            className={
                'inline-flex min-h-11 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition md:min-h-0 ' +
                (activo
                    ? 'border-neutral-600 bg-neutral-800/80 text-neutral-100'
                    : 'border-neutral-800 text-neutral-600 line-through hover:text-neutral-400')
            }
        >
            {color && (
                <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: activo ? color : 'transparent', border: `1.5px solid ${color}` }}
                />
            )}
            {children}
        </button>
    )
}
